import * as React from "react"
import BaseController from "../controllers/BaseController"
import { EDITOR_MESSAGE_SOURCE, EditorToHostMessage, HOST_MESSAGE_SOURCE } from "../../embeddable_editor/protocol"

interface IProps {
  value: string
  placeholder?: string
  onChange: (html: string) => void
}

// The widget's own BASE_URL (e.g. https://widget.bizzey.com or
// http://widget.localhost.test:3000) always points at the same environment
// the tour builder is running against, so swapping the "widget" subdomain
// for "editor" gives us the matching editor host for free - no separate
// setting needs to be threaded through the install snippet.
const editorOrigin = (): string => {
  const url = new URL(BaseController.BASE_URL)
  url.hostname = url.hostname.replace(/^widget\./, "editor.")
  return url.origin
}

// Renders the licensed Froala editor (hosted on editor.bizzey.com, see
// src/embeddable_editor/Application.tsx) inside an iframe, since loading
// Froala directly here would run it on the customer's origin and violate
// our domain-locked license.
const EmbeddedEditorFrame = ({ value, placeholder, onChange }: IProps) => {
  const iframeRef = React.useRef<HTMLIFrameElement>(null)
  const initialValue = React.useRef(value)

  const src = React.useMemo(() => {
    const params = new URLSearchParams({ toolbarInline: "true", heightMin: "80" })
    if (placeholder) params.set("placeholderText", placeholder)
    return `${editorOrigin()}/?${params.toString()}`
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return

      const data = event.data as EditorToHostMessage
      if (!data || data.source !== EDITOR_MESSAGE_SOURCE) return

      if (data.type === "ready") {
        iframeRef.current?.contentWindow?.postMessage(
          { source: HOST_MESSAGE_SOURCE, type: "setContent", html: initialValue.current },
          "*",
        )
      } else if (data.type === "change") {
        onChange(data.html)
      }
    }

    window.addEventListener("message", onMessage)
    return () => window.removeEventListener("message", onMessage)
  }, [onChange])

  return (
    <iframe
      ref={iframeRef}
      className="bizzey-tb-editor-frame"
      src={src}
      title="Rich text editor"
    />
  )
}

export default EmbeddedEditorFrame
